import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ClientBottomNav from "@/components/ClientBottomNav";
import EmptyState from "@/components/EmptyState";
import { Badge } from "@/components/ui/badge";
import { getCategoryColorsByLabel } from "@/lib/categoryColors";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Search, MapPin, Wrench, Users, Heart, PlusCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { useState } from "react";
import { useSearchArtisans, specialites, type SortOption } from "@/hooks/useSearchArtisans";
import { HANDYMAN_SPECIALTIES, getHandymanLabel, type HandymanSpecialtyCode } from "@/lib/handymanConfig";
import ArtisanBadges, { ArtisanSignals } from "@/components/ArtisanBadges";
import VerificationBadge from "@/components/VerificationBadge";
import {
  useFavoriteArtisanIds,
  useFavoriteArtisans,
  useToggleFavoriteArtisan,
} from "@/hooks/useFavoriteArtisans";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";
import communes from "@/data/communes.json";
import { useWilayas, getCommuneLabel, type Commune } from "@/hooks/useAlgeriaData";
import { cn } from "@/lib/utils";
import { useSEO } from "@/hooks/useSEO";
import { useLanguage } from "@/hooks/useLanguage";
import { useLocalizedPath } from "@/hooks/useLocalizedNavigate";

type Tab = "search" | "favoris";

const RechercheArtisans = () => {
  const { t, locale } = useLanguage();
  const lp = useLocalizedPath();
  const allWilayas = useWilayas(locale);

  const sortOptions = [
    { value: "relevance", label: t("search.sort_relevance") },
    { value: "response",  label: t("search.sort_response") },
    { value: "recent",    label: t("search.sort_recent") },
  ];

  useSEO({
    title: locale === "ar"
      ? "البحث عن حرفي في الجزائر — سبّاك، كهربائي، بنّاء"
      : "Trouver un artisan en Algérie — Plombier, Électricien, Maçon",
    description: locale === "ar"
      ? "ابحث عن حرفي مؤهّل بالقرب منك في جميع ولايات الجزائر. سبّاكون وكهربائيون وبنّاؤون موثّقون. مجاني وبدون عمولة."
      : "Trouvez un artisan qualifié près de chez vous dans toutes les wilayas d'Algérie. Plombiers, électriciens, maçons et plus. Gratuit et sans commission.",
    canonical: "/recherche-artisans",
    locale,
  });

  const [activeTab, setActiveTab] = useState<Tab>("search");
  const [filterWilaya, setFilterWilaya] = useState("toutes");
  const [filterCommune, setFilterCommune] = useState("toutes");
  const [filterSpecialite, setFilterSpecialite] = useState("tous");
  const [filterHandymanSpecialty, setFilterHandymanSpecialty] = useState("toutes");
  const [sortBy, setSortBy] = useState<SortOption>("relevance");
  const [searchQuery, setSearchQuery] = useState("");

  const { user } = useAuth();
  const { data: favoriteIds } = useFavoriteArtisanIds();
  const { data: favoriteArtisans = [], isLoading: isLoadingFavs } = useFavoriteArtisans();
  const toggleFavorite = useToggleFavoriteArtisan();

  const handleWilayaChange = (value: string) => {
    setFilterWilaya(value);
    setFilterCommune("toutes");
  };

  const handleSpecialiteChange = (value: string) => {
    setFilterSpecialite(value);
    setFilterHandymanSpecialty("toutes");
  };

  const communesTyped = communes as Commune[];

  const selectedStateId = allWilayas.find(w => w.state_name === filterWilaya)?.state_id;
  const communesDispo = selectedStateId
    ? communesTyped.filter(c => c.state_id === selectedStateId)
    : [];

  const { data: artisans = [], isLoading } = useSearchArtisans({
    wilaya:             filterWilaya !== "toutes" ? filterWilaya : undefined,
    commune:            filterCommune !== "toutes" ? filterCommune : undefined,
    specialite:         filterSpecialite !== "tous" ? filterSpecialite : undefined,
    handymanSpecialty:  filterHandymanSpecialty !== "toutes" ? filterHandymanSpecialty : undefined,
    search:             searchQuery || undefined,
    sort:               sortBy,
  });

  const handleToggleFavorite = (artisanUserId: string) => {
    if (!user) {
      toast({
        title: t("search.login_required"),
        description: t("search.login_required_hint"),
        variant: "destructive",
      });
      return;
    }
    toggleFavorite.mutate(artisanUserId);
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="container py-8 pb-24 md:pb-8">

        <div className="mb-6">
          <h1 className="font-display text-3xl font-bold">{t("search.title")}</h1>
          <p className="mt-1 text-muted-foreground">{t("search.subtitle")}</p>
        </div>

        {/* Onglets */}
        <div className="mb-6 flex border-b">
          <button
            onClick={() => setActiveTab("search")}
            className={cn(
              "pb-3 px-4 text-sm font-medium border-b-2 -mb-px transition-colors",
              activeTab === "search"
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            )}
          >
            {t("search.tab_search")}
          </button>
          {user && (
            <button
              onClick={() => setActiveTab("favoris")}
              className={cn(
                "pb-3 px-4 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-2",
                activeTab === "favoris"
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              )}
            >
              {t("search.tab_favorites")}
              {favoriteArtisans.length > 0 && (
                <Badge className="h-4 min-w-4 rounded-full bg-primary/10 text-primary border-0 px-1.5 text-[10px] leading-none">
                  {favoriteArtisans.length}
                </Badge>
              )}
            </button>
          )}
        </div>

        {/* ── Onglet Rechercher ── */}
        {activeTab === "search" && (
          <>
            <div className="mb-8 flex flex-col gap-3 rounded-xl border bg-card p-4 sm:flex-row sm:items-center sm:flex-wrap">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder={t("search.placeholder")}
                  className="ps-9"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <Select value={filterWilaya} onValueChange={handleWilayaChange}>
                <SelectTrigger className="w-full sm:w-[180px]">
                  <SelectValue placeholder={t("search.all_wilayas")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="toutes">{t("search.all_wilayas")}</SelectItem>
                  {allWilayas
                    .sort((a, b) => a.label.localeCompare(b.label, locale === "ar" ? "ar" : "fr"))
                    .map((w) => (
                      <SelectItem key={w.state_id} value={w.state_name}>{w.label}</SelectItem>
                    ))}
                </SelectContent>
              </Select>
              {filterWilaya !== "toutes" && (
                <Select value={filterCommune} onValueChange={setFilterCommune}>
                  <SelectTrigger className="w-full sm:w-[180px]">
                    <SelectValue placeholder={t("search.all_communes")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="toutes">{t("search.all_communes")}</SelectItem>
                    {communesDispo.map(c => (
                      <SelectItem key={c.city_id} value={c.city_name}>{getCommuneLabel(c, locale)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Select value={filterSpecialite} onValueChange={handleSpecialiteChange}>
                <SelectTrigger className="w-full sm:w-[200px]">
                  <SelectValue placeholder={t("categories.plumbing")} />
                </SelectTrigger>
                <SelectContent>
                  {specialites.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{locale === "ar" ? s.label_ar : s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {filterSpecialite === "Artisan polyvalent" && (
                <Select value={filterHandymanSpecialty} onValueChange={setFilterHandymanSpecialty}>
                  <SelectTrigger className="w-full sm:w-[220px]">
                    <SelectValue placeholder={t("search.all_sub_specialites")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="toutes">{t("search.all_sub_specialites")}</SelectItem>
                    {HANDYMAN_SPECIALTIES.map((s) => (
                      <SelectItem key={s.code} value={s.code}>{locale === "ar" ? s.label_ar : s.label_fr}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Select value={sortBy} onValueChange={(v) => setSortBy(v as SortOption)}>
                <SelectTrigger className="w-full sm:w-[180px]">
                  <SelectValue placeholder={t("search.sort_label")} />
                </SelectTrigger>
                <SelectContent>
                  {sortOptions.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {isLoading ? (
              <p className="text-center text-muted-foreground py-12">{t("search.loading")}</p>
            ) : artisans.length === 0 ? (
              <Card>
                <CardContent className="p-0">
                  <EmptyState
                    icon={Users}
                    title={
                      filterWilaya !== "toutes" && filterSpecialite !== "tous"
                        ? `Aucun ${filterSpecialite.toLowerCase()} disponible à ${filterWilaya} pour le moment.`
                        : filterWilaya !== "toutes"
                        ? `Les artisans arrivent bientôt à ${filterWilaya}.`
                        : filterSpecialite !== "tous"
                        ? `Aucun artisan en ${filterSpecialite.toLowerCase()} trouvé pour le moment.`
                        : "Les artisans arrivent bientôt dans votre région."
                    }
                    description="Publiez votre demande et soyez contacté dès qu'un artisan qualifié est disponible."
                    primaryAction={{
                      label: "Publier ma demande",
                      href: lp("/poster-annonce"),
                    }}
                    secondaryAction={{
                      label: "Modifier ma recherche",
                      onClick: () => {
                        setFilterWilaya("toutes");
                        setFilterSpecialite("tous");
                        setSearchQuery("");
                      },
                    }}
                  />
                </CardContent>
              </Card>
            ) : (
              <>
                <p className="mb-4 text-sm text-muted-foreground">
                  {artisans.length <= 1
                    ? t("search.count_one", { count: artisans.length })
                    : t("search.count_other", { count: artisans.length })}
                </p>
                <div className="grid gap-4 md:grid-cols-2">
                  {artisans.map((artisan) => {
                    const isFav = favoriteIds?.has(artisan.user_id);
                    return (
                      <Card key={artisan.id} className="group overflow-hidden transition-all hover:shadow-lg hover:border-primary/20 h-full">
                        <CardContent className="flex gap-4 p-4">
                          <Link
                            to={lp(`/artisan/${artisan.user_id}`)}
                            className="flex h-16 w-16 shrink-0 items-center justify-center rounded-xl bg-secondary overflow-hidden"
                          >
                            {artisan.logo_url ? (
                              <img src={artisan.logo_url} alt={artisan.display_name} className="h-full w-full object-cover" />
                            ) : (
                              <Wrench className="h-8 w-8 text-muted-foreground" />
                            )}
                          </Link>
                          <div className="flex-1 space-y-1.5 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <Link to={lp(`/artisan/${artisan.user_id}`)} className="min-w-0">
                                <h3 className="font-display font-semibold leading-tight truncate hover:text-primary transition-colors">
                                  {artisan.type === "entreprise" && artisan.raison_sociale
                                    ? artisan.raison_sociale
                                    : artisan.display_name}
                                </h3>
                              </Link>
                              <div className="flex items-center gap-1 shrink-0">
                                <button
                                  onClick={() => handleToggleFavorite(artisan.user_id)}
                                  className="p-1.5 rounded-lg hover:bg-muted transition-colors"
                                  aria-label={isFav ? t("search.remove_fav") : t("search.add_fav")}
                                >
                                  <Heart className={cn("h-4 w-4 transition-colors", isFav ? "fill-destructive text-destructive" : "text-muted-foreground")} />
                                </button>
                                <VerificationBadge
                                  type={artisan.type}
                                  verificationStatus={artisan.verification_status ?? undefined}
                                  className="text-xs"
                                />
                              </div>
                            </div>
                            <div className="flex items-center gap-1.5 flex-wrap">
                              {(() => { const c = getCategoryColorsByLabel(artisan.specialite); return (
                              <span className={cn("inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium border", c.badgeBg, c.badgeText)}>
                                <Wrench className="h-3 w-3" />{artisan.specialite}
                              </span>); })()}
                              {artisan.is_polyvalent ? (
                                <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 border-accent text-accent">{t("search.generalist")}</Badge>
                              ) : (
                                <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 border-primary text-primary">{t("search.specialist")}</Badge>
                              )}
                            </div>
                            {artisan.is_polyvalent && artisan.handyman_specialties && artisan.handyman_specialties.length > 0 && (
                              <div className="flex flex-wrap gap-1">
                                {artisan.handyman_specialties.slice(0, 3).map((code) => (
                                  <Badge key={code} variant="secondary" className="text-[10px] px-1.5 py-0 h-4">
                                    {getHandymanLabel(code as HandymanSpecialtyCode)}
                                  </Badge>
                                ))}
                                {artisan.handyman_specialties.length > 3 && (
                                  <span className="text-[10px] text-muted-foreground">+{artisan.handyman_specialties.length - 3}</span>
                                )}
                              </div>
                            )}
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              <MapPin className="h-3 w-3" />
                              <span>{artisan.commune}, {artisan.wilaya}</span>
                            </div>
                            <ArtisanBadges
                              isReactiveBadge={artisan.is_reactive_badge ?? undefined}
                              profileCompletionScore={artisan.profile_completion_score ?? undefined}
                            />
                            <ArtisanSignals
                              responseRate={artisan.response_rate ? Number(artisan.response_rate) : undefined}
                              avgResponseTimeMinutes={artisan.avg_response_time_minutes ? Number(artisan.avg_response_time_minutes) : undefined}
                              lastLoginAt={artisan.last_login_at ?? undefined}
                            />
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </>
            )}
          </>
        )}

        {/* ── Onglet Mes favoris ── */}
        {activeTab === "favoris" && (
          <>
            {!user ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Heart className="mx-auto h-12 w-12 text-muted-foreground/30" />
                  <p className="mt-3 font-medium">{t("search.login_favorites")}</p>
                  <p className="text-sm text-muted-foreground mb-4">{t("search.login_favorites_hint")}</p>
                  <Link to={lp("/connexion")}>
                    <Button variant="hero">{t("search.login_btn")}</Button>
                  </Link>
                </CardContent>
              </Card>
            ) : isLoadingFavs ? (
              <p className="text-center text-muted-foreground py-12">{t("search.loading")}</p>
            ) : favoriteArtisans.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Heart className="mx-auto h-12 w-12 text-muted-foreground/30" />
                  <p className="mt-3 text-muted-foreground">{t("search.no_favorites")}</p>
                  <p className="text-sm text-muted-foreground mb-4">
                    {t("search.no_favorites_hint")}
                  </p>
                  <Button variant="outline" onClick={() => setActiveTab("search")}>
                    {t("search.browse")}
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <>
                <p className="mb-4 text-sm text-muted-foreground">
                  {favoriteArtisans.length <= 1
                    ? t("search.fav_count_one", { count: favoriteArtisans.length })
                    : t("search.fav_count_other", { count: favoriteArtisans.length })}
                </p>
                <div className="grid gap-4 md:grid-cols-2">
                  {favoriteArtisans.map((fav) => {
                    const artisan = fav.artisan;
                    const profile = fav.profile;
                    if (!artisan) return null;
                    return (
                      <Card key={fav.id} className="group overflow-hidden transition-all hover:shadow-lg hover:border-primary/20">
                        <CardContent className="flex gap-4 p-4">
                          <Link
                            to={lp(`/artisan/${fav.artisan_user_id}`)}
                            className="flex h-16 w-16 shrink-0 items-center justify-center rounded-xl bg-secondary overflow-hidden"
                          >
                            {artisan.logo_url ? (
                              <img src={artisan.logo_url} alt={profile?.display_name || ""} className="h-full w-full object-cover" />
                            ) : (
                              <Wrench className="h-8 w-8 text-muted-foreground" />
                            )}
                          </Link>
                          <div className="flex-1 space-y-1.5 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <Link to={lp(`/artisan/${fav.artisan_user_id}`)} className="min-w-0">
                                <h3 className="font-display font-semibold leading-tight truncate hover:text-primary transition-colors">
                                  {profile?.display_name || "Artisan"}
                                </h3>
                                {artisan.type === "entreprise" && artisan.raison_sociale && (
                                  <p className="text-xs text-muted-foreground truncate">{artisan.raison_sociale}</p>
                                )}
                              </Link>
                              <button
                                onClick={() => toggleFavorite.mutate(fav.artisan_user_id)}
                                className="p-1.5 -m-1 shrink-0 rounded-lg hover:bg-muted transition-colors"
                                aria-label={t("search.remove_fav")}
                              >
                                <Heart className="h-4 w-4 fill-destructive text-destructive" />
                              </button>
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                              <VerificationBadge
                                type={artisan.type}
                                verificationStatus={(artisan as any).verification_status}
                                className="text-xs"
                              />
                              {(() => { const c = getCategoryColorsByLabel(artisan.specialite); return (
                              <span className={cn("inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium border", c.badgeBg, c.badgeText)}>
                                <Wrench className="h-3 w-3" />{artisan.specialite}
                              </span>); })()}
                            </div>
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              <MapPin className="h-3 w-3" />
                              <span>{artisan.commune}, {artisan.wilaya}</span>
                            </div>
                            <Link to={lp(`/artisan/${fav.artisan_user_id}`)}>
                              <Button variant="outline" size="sm" className="mt-1 h-7 text-xs gap-1">
                                {t("search.view_profile")} <DirectionalArrow className="h-3 w-3" />
                              </Button>
                            </Link>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </>
            )}
          </>
        )}

      </main>
      <Footer />
      <ClientBottomNav />
    </div>
  );
};

export default RechercheArtisans;
